import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { X, Check, Calendar, User as UserIcon, Layout, Briefcase, ChevronRight, History, Trash2 } from 'lucide-react';

const TaskModal = ({ isOpen, onClose, onSuccess, taskToEdit = null }) => {
    const queryClient = useQueryClient();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        project_id_db: '', // Local Integer ID
        product_id: '',    // Local Integer ID
        priority: 'Medium',
        assignee_id: '',   // Local User ID
        deadline: '',
        is_internal: true
    });
    const [activeTab, setActiveTab] = useState('details'); // details or history
    const [projectProducts, setProjectProducts] = useState([]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);



    // Fetch Settings for defaults
    const { data: settings } = useQuery({
        queryKey: ['settings'],
        queryFn: async () => (await client.get('/settings')).data,
        enabled: isOpen,
        refetchOnWindowFocus: false
    });

    // Fetch local projects
    const { data: localProjects } = useQuery({
        queryKey: ['projects'],
        queryFn: async () => (await client.get('/projects')).data,
        enabled: isOpen,
        refetchOnWindowFocus: false
    });

    // Fetch local users
    const { data: appUsers } = useQuery({
        queryKey: ['users'],
        queryFn: async () => (await client.get('/users')).data,
        enabled: isOpen,
        refetchOnWindowFocus: false
    });

    // Fetch Task History
    const { data: history = [], isLoading: isHistoryLoading } = useQuery({
        queryKey: ['task-history', taskToEdit?.id],
        queryFn: async () => (await client.get(`/tasks/${taskToEdit.id}/history`)).data,
        enabled: !!taskToEdit && activeTab === 'history',
        refetchOnWindowFocus: false
    });

    useEffect(() => {
        if (!isOpen) {
            setFormData({
                title: '',
                description: '',
                project_id_db: '',
                product_id: '',
                priority: 'Medium',
                status: 'Новая',
                assignee_id: '',
                deadline: '',
                is_internal: true
            });
        } else if (taskToEdit) {
            // Edit Mode
            setFormData({
                title: taskToEdit.title || '',
                description: taskToEdit.description || '',
                project_id_db: taskToEdit.project_id_db || '',
                product_id: taskToEdit.product_id || '',
                priority: taskToEdit.priority || 'Medium',
                status: taskToEdit.status || 'Новая',
                assignee_id: taskToEdit.assignee_id || '',
                deadline: taskToEdit.deadline ? new Date(taskToEdit.deadline).toISOString().slice(0, 16) : '',
                is_internal: true
            });
        } else if (settings) {
            // Apply defaults for New Task
            setFormData(prev => ({
                ...prev,
                is_internal: true
            }));
        }
    }, [isOpen, settings, taskToEdit]);

    const mutation = useMutation({
        mutationFn: (data) => taskToEdit 
            ? client.patch(`/tasks/${taskToEdit.id}`, data)
            : client.post('/tasks', data),
        onSuccess: () => {
            if (onSuccess) onSuccess();
            onClose();
        },
        onError: (error) => {
            const msg = error.response?.data?.detail || error.message;
            alert('Ошибка при создании задачи: ' + msg);
        }
    });

    // Find services for selected local project
    const currentLocalProject = localProjects?.find(p => p.id === parseInt(formData.project_id_db));

    useEffect(() => {
        if (currentLocalProject) {
            let products = [];
            if (currentLocalProject.custom_products) {
                try {
                    const cp = typeof currentLocalProject.custom_products === 'string' 
                        ? JSON.parse(currentLocalProject.custom_products) 
                        : currentLocalProject.custom_products;
                    products = [...cp];
                } catch(e) { console.error("Error parsing custom products", e); }
            }
            setProjectProducts(products);
        } else {
            setProjectProducts([]);
        }
    }, [currentLocalProject]);

    // Final check for render
    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        
        mutation.mutate({
            title: formData.title,
            description: formData.description,
            project_id_db: formData.project_id_db ? parseInt(formData.project_id_db) : null,
            product_id: formData.product_id ? parseInt(formData.product_id) : null,
            priority: formData.priority,
            status: formData.status,
            is_internal: true,
            assignee_id: formData.assignee_id ? parseInt(formData.assignee_id) : null,
            deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null
        });
    };

    return (
        <div className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex ${isMobile ? 'items-end' : 'items-center justify-center'} p-0 md:p-4`}>
            <div
                className={`bg-neutral-900 border-x border-t md:border border-neutral-800 w-full max-w-xl 
                    ${isMobile ? 'rounded-t-[2.5rem] animate-in slide-in-from-bottom duration-300' : 'rounded-3xl animate-in zoom-in-95 duration-200'} 
                    shadow-2xl overflow-hidden max-h-[92dvh] flex flex-col`}
            >
                {/* Mobile Handle */}
                {isMobile && (
                    <div className="w-full flex justify-center pt-3 shrink-0">
                        <div className="w-12 h-1 bg-neutral-800 rounded-full" />
                    </div>
                )}

                <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar">
                    <div className="flex justify-between items-center mb-4 md:mb-6 sticky top-0 bg-neutral-900/95 backdrop-blur z-10 py-2 -mt-2">
                        <div className="flex flex-col">
                            <h3 className="text-xl font-black text-white uppercase tracking-tight italic">
                                {taskToEdit ? 'Задача' : 'Новая задача'}
                            </h3>
                            {taskToEdit && (
                                <div className="flex gap-4 mt-2">
                                    <button 
                                        onClick={() => setActiveTab('details')}
                                        className={`text-[10px] font-black uppercase tracking-widest pb-1 transition-all ${activeTab === 'details' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-neutral-500'}`}
                                    >
                                        Детали
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('history')}
                                        className={`text-[10px] font-black uppercase tracking-widest pb-1 transition-all ${activeTab === 'history' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-neutral-500'}`}
                                    >
                                        История
                                    </button>
                                </div>
                            )}
                        </div>
                        <button onClick={onClose} className="p-2 bg-neutral-800/50 hover:bg-neutral-800 rounded-full transition-all text-neutral-500 hover:text-white">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {activeTab === 'details' ? (
                        <form onSubmit={handleSubmit} className="space-y-6 pb-10 md:pb-4">


                            {/* Status Selector (Internal Only) */}
                            {taskToEdit && formData.is_internal && (
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Статус</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500 transition-all font-bold appearance-none"
                                    >
                                        {['Новая', 'В работе', 'На согласовании', 'Готово'].map(s => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Title */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Заголовок задачи</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-[2rem] outline-none focus:border-blue-500 transition-all font-bold text-lg placeholder:text-neutral-700"
                                    placeholder="Что нужно сделать?"
                                />
                            </div>

                            {/* Description */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Описание / Чек-лист</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-[2rem] outline-none focus:border-blue-500 transition-all font-medium text-sm placeholder:text-neutral-700 min-h-[120px] resize-none"
                                    placeholder="Детали задачи..."
                                />
                            </div>

                            {/* Local Hierarchy Selection */}
                            <div className="space-y-4 bg-neutral-800/30 p-4 rounded-[2.5rem] border border-neutral-800/50">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                            <Briefcase size={12} className="text-blue-500" /> Проект (ERP)
                                        </label>
                                        <select
                                            value={formData.project_id_db}
                                            onChange={(e) => setFormData({ ...formData, project_id_db: e.target.value, product_id: '' })}
                                            className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500/50 transition-all font-bold appearance-none text-xs"
                                        >
                                            <option value="">Выберите проект...</option>
                                            {localProjects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                            <Layout size={12} className="text-emerald-500" /> Продукт
                                        </label>
                                        <select
                                            value={formData.product_id}
                                            onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                                            disabled={!formData.project_id_db}
                                            className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500/50 transition-all font-bold appearance-none text-xs disabled:opacity-50"
                                        >
                                            <option value="">{formData.project_id_db ? 'Выберите продукт...' : 'Сначала выберите проект'}</option>
                                            {projectProducts?.map((p, idx) => (
                                                <option key={p.id || idx} value={p.id}>{p.name || p.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>



                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Priority & Assignee */}
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Приоритет</label>
                                        <div className="flex gap-2">
                                            {['Low', 'Medium', 'High', 'Critical'].map(p => (
                                                <button
                                                    key={p}
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, priority: p })}
                                                    className={`flex-1 py-3 rounded-xl text-[9px] font-black uppercase tracking-tighter transition-all border-2 ${formData.priority === p 
                                                        ? (p === 'Critical' ? 'bg-rose-600 border-rose-600 text-white' : 
                                                           p === 'High' ? 'bg-amber-600 border-amber-600 text-white' : 
                                                           p === 'Medium' ? 'bg-blue-600 border-blue-600 text-white' : 
                                                           'bg-emerald-600 border-emerald-600 text-white')
                                                        : 'bg-neutral-800 border-neutral-800 text-neutral-500 hover:border-neutral-700'
                                                    }`}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                            <UserIcon size={12} className="text-amber-500" /> Ответственный (ERP)
                                        </label>
                                        <select
                                            value={formData.assignee_id}
                                            onChange={(e) => setFormData({ ...formData, assignee_id: e.target.value })}
                                            className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500 transition-all font-bold appearance-none text-sm"
                                        >
                                            <option value="">Выберите сотрудника...</option>
                                            {appUsers?.map(u => <option key={u.id} value={u.id}>{u.fio || u.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* Deadline & Yougile Assignees */}
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                            <Calendar size={12} className="text-rose-500" /> Дедлайн
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={formData.deadline}
                                            onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                                            className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500/50 transition-all font-bold appearance-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className={`flex gap-3 pt-6 ${isMobile ? 'sticky bottom-0 bg-neutral-900/95 backdrop-blur-xl py-4 pb-8 md:pb-4' : ''}`}>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 bg-neutral-800 hover:bg-neutral-700 text-neutral-500 font-black uppercase text-[10px] tracking-widest py-5 rounded-3xl transition-all"
                                >
                                    {taskToEdit ? 'Закрыть' : 'Отмена'}
                                </button>
                                {taskToEdit && formData.is_internal && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (window.confirm('Удалить эту задачу?')) {
                                                client.delete(`/tasks/${taskToEdit.id}`).then(() => {
                                                    onSuccess();
                                                    onClose();
                                                });
                                            }
                                        }}
                                        className="p-5 bg-rose-600/20 hover:bg-rose-600 text-rose-500 hover:text-white rounded-3xl transition-all"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    disabled={mutation.isPending}
                                    className="flex-2 bg-blue-600 hover:bg-blue-500 shadow-blue-600/20 text-white font-black uppercase text-[10px] tracking-widest py-5 rounded-3xl transition-all shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {mutation.isPending ? 'Загрузка...' : (
                                        <>
                                            <Check className="w-4 h-4" />
                                            {taskToEdit ? 'Обновить' : 'Создать задачу'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    ) : (
                        /* History View */
                        <div className="space-y-4 pb-10">
                            {isHistoryLoading ? (
                                <div className="py-12 text-center text-neutral-500 text-xs font-bold uppercase tracking-widest">Загрузка истории...</div>
                            ) : history.length === 0 ? (
                                <div className="py-12 text-center text-neutral-500 text-xs font-bold uppercase tracking-widest">История пуста</div>
                            ) : (
                                <div className="relative border-l-2 border-neutral-800 ml-4 space-y-8 py-4">
                                    {history.map((h, i) => (
                                        <div key={h.id} className="relative pl-8">
                                            {/* Dot */}
                                            <div className="absolute -left-[11px] top-1 w-5 h-5 bg-neutral-900 border-2 border-amber-500 rounded-full flex items-center justify-center">
                                                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] text-neutral-500 font-bold uppercase mt-0.5">
                                                        {new Date(h.created_at).toLocaleString('ru-RU')}
                                                    </span>
                                                    <span className="text-[10px] text-blue-400 font-black uppercase">
                                                        {h.user_name || 'Система'}
                                                    </span>
                                                </div>
                                                <div className="text-sm text-neutral-200 mt-1">
                                                    Изменил статус с <span className="font-bold text-neutral-400">"{h.old_status}"</span> на <span className="font-bold text-white">"{h.new_status}"</span>
                                                </div>
                                                {h.comment && (
                                                    <div className="mt-2 p-3 bg-neutral-800/50 rounded-xl border border-neutral-700/50 text-xs text-neutral-400 italic">
                                                        "{h.comment}"
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <button
                                onClick={() => setActiveTab('details')}
                                className="w-full mt-8 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 font-black uppercase text-[10px] tracking-widest py-4 rounded-2xl transition-all"
                            >
                                Вернуться к деталям
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TaskModal;
